/**
 * Error message sanitization and user-friendly conversion utilities
 *
 * This module provides functions to clean and convert technical error messages
 * from the backend into user-friendly messages suitable for display in toast notifications.
 */

/**
 * Sanitizes error messages by removing HTML tags and making them user-friendly
 *
 * @param error - The error object from API calls (can be AxiosError or any error)
 * @param fallback - Fallback message if no error message can be extracted
 * @returns A clean, user-friendly error message
 */
export function sanitizeErrorMessage(error: any, fallback: string = 'An error occurred'): string {
  let message = fallback;

  // Extract message from various error formats (priority order)

  // Check for field-specific errors object (Rails validation errors)
  // Check both paths: direct errors and wrapped in data (due to render_json_response wrapper)
  const errorsObj = error?.response?.data?.errors || error?.response?.data?.data?.errors;
  if (Array.isArray(errorsObj) && errorsObj.length > 0) {
    // Rails full_messages array, e.g. ["Email is already linked to another account"]
    message = String(errorsObj[0]);
  } else if (errorsObj && typeof errorsObj === 'object') {
    const errors = errorsObj;
    // Get first error message from the errors object
    const firstField = Object.keys(errors)[0];
    if (firstField && Array.isArray(errors[firstField]) && errors[firstField].length > 0) {
      message = errors[firstField][0];
    }
  }
  // Check for messages array (both paths)
  else if ((error?.response?.data?.messages && Array.isArray(error.response.data.messages) && error.response.data.messages.length > 0) ||
           (error?.response?.data?.data?.messages && Array.isArray(error.response.data.data.messages) && error.response.data.data.messages.length > 0)) {
    message = error.response.data.messages?.[0] || error.response.data.data?.messages?.[0];
  }
  // Check for full_messages array (both paths)
  else if ((error?.response?.data?.full_messages && Array.isArray(error.response.data.full_messages) && error.response.data.full_messages.length > 0) ||
           (error?.response?.data?.data?.full_messages && Array.isArray(error.response.data.data.full_messages) && error.response.data.data.full_messages.length > 0)) {
    message = error.response.data.full_messages?.[0] || error.response.data.data?.full_messages?.[0];
  }
  // Check for nested error message
  else if (error?.response?.data?.error?.message) {
    message = error.response.data.error.message;
  } else if (error?.response?.data?.message) {
    message = error.response.data.message;
  } else if (error?.response?.data?.error) {
    // Sometimes the error is just a string
    message = typeof error.response.data.error === 'string'
      ? error.response.data.error
      : fallback;
  } else if (error?.message) {
    message = error.message;
  }

  // Remove HTML tags (including <p>, <div>, <span>, etc.)
  message = message.replace(/<[^>]*>/g, '');

  // Remove HTML entities
  message = message.replace(/&lt;/g, '<')
                   .replace(/&gt;/g, '>')
                   .replace(/&amp;/g, '&')
                   .replace(/&quot;/g, '"')
                   .replace(/&#39;/g, "'");

  // Convert technical messages to user-friendly ones
  message = makeUserFriendly(message);

  // Clean up extra whitespace
  message = message.replace(/\s+/g, ' ').trim();

  return message;
}

/**
 * Converts technical error messages to user-friendly ones
 *
 * @param message - The technical error message
 * @returns A user-friendly error message
 */
function makeUserFriendly(message: string): string {
  // Map common technical messages to friendly ones
  const friendlyMappings: Record<string, string> = {
    'Validation failed': 'Please check your input and try again',
    'Record not found': 'The requested item was not found',
    'Not found': 'The requested item could not be found',
    'Unauthorized': 'You do not have permission to perform this action',
    'Forbidden': 'Access denied. You do not have the necessary permissions',
    'Internal server error': 'Something went wrong on our end. Please try again later',
    'Service unavailable': 'The service is temporarily unavailable. Please try again shortly',
    'Bad request': 'Invalid request. Please check your input',
    'Network error': 'Unable to connect. Please check your internet connection',
    'Timeout': 'The request took too long. Please try again',
    // Rails-specific messages
    "can't be blank": 'is required',
    'has already been taken': 'is already in use',
    'is too short': 'must be longer',
    'is too long': 'must be shorter',
    'is not included in the list': 'is invalid',
    'must be accepted': 'must be accepted',
    'is not a number': 'must be a number',
  };

  // Check for exact matches first
  for (const [technical, friendly] of Object.entries(friendlyMappings)) {
    if (message.toLowerCase() === technical.toLowerCase()) {
      return friendly;
    }
  }

  // Check for partial matches
  for (const [technical, friendly] of Object.entries(friendlyMappings)) {
    if (message.toLowerCase().includes(technical.toLowerCase())) {
      // For Rails validation errors, preserve the field name
      if (technical.includes("can't be blank") ||
          technical.includes('has already been taken') ||
          technical.includes('is too short') ||
          technical.includes('is too long')) {
        return message.replace(new RegExp(technical, 'gi'), friendly);
      }
      return friendly;
    }
  }

  return message;
}

/**
 * Extracts validation errors from backend response
 *
 * @param error - The error object from API calls
 * @returns An object mapping field names to error messages, or null if no validation errors
 */
export function extractValidationErrors(error: any): Record<string, string> | null {
  const errors = error?.response?.data?.error?.errors ||
                 error?.response?.data?.errors ||
                 null;

  if (!errors || typeof errors !== 'object') return null;

  // Convert to user-friendly format
  const friendlyErrors: Record<string, string> = {};

  for (const [field, messages] of Object.entries(errors)) {
    let errorMessage: string;

    if (Array.isArray(messages)) {
      // Join multiple messages and sanitize
      errorMessage = messages.map(msg => sanitizeMessage(String(msg))).join(', ');
    } else {
      errorMessage = sanitizeMessage(String(messages));
    }

    friendlyErrors[field] = errorMessage;
  }

  return friendlyErrors;
}

/**
 * Sanitizes a single error message string
 *
 * @param message - The message to sanitize
 * @returns A clean message without HTML tags
 */
function sanitizeMessage(message: string): string {
  // Remove HTML tags
  let clean = message.replace(/<[^>]*>/g, '');

  // Remove HTML entities
  clean = clean.replace(/&lt;/g, '<')
               .replace(/&gt;/g, '>')
               .replace(/&amp;/g, '&')
               .replace(/&quot;/g, '"')
               .replace(/&#39;/g, "'");

  // Clean up whitespace
  clean = clean.replace(/\s+/g, ' ').trim();

  return clean;
}

/**
 * Gets a user-friendly error message based on HTTP status code
 *
 * @param statusCode - The HTTP status code
 * @param defaultMessage - Default message if no specific message is available
 * @returns A user-friendly error message
 */
export function getErrorMessageByStatus(statusCode: number, defaultMessage?: string): string {
  const statusMessages: Record<number, string> = {
    400: 'Invalid request. Please check your input and try again',
    401: 'Your session has expired. Please log in again',
    403: 'You do not have permission to perform this action',
    404: 'The requested resource was not found',
    409: 'This action conflicts with existing data',
    422: 'Please check your input and correct any errors',
    429: 'Too many requests. Please slow down and try again',
    500: 'Something went wrong on our end. Please try again later',
    502: 'Service temporarily unavailable. Please try again shortly',
    503: 'Service temporarily unavailable. Please try again shortly',
    504: 'The request timed out. Please try again',
  };

  return statusMessages[statusCode] || defaultMessage || 'An unexpected error occurred';
}

/**
 * Checks if an error is a network error (no response from server)
 *
 * @param error - The error object
 * @returns True if it's a network error
 */
export function isNetworkError(error: any): boolean {
  return !error?.response && (
    error?.message?.includes('Network Error') ||
    error?.message?.includes('Network error') ||
    error?.code === 'ECONNABORTED' ||
    error?.code === 'ERR_NETWORK'
  );
}

/**
 * Gets a complete error message with context
 * Combines status code messages with specific error details
 *
 * @param error - The error object
 * @param fallback - Fallback message
 * @returns A comprehensive error message
 */
export function getErrorMessage(error: any, fallback: string = 'An error occurred'): string {
  // Check if it's a network error
  if (isNetworkError(error)) {
    return 'Unable to connect to the server. Please check your internet connection';
  }

  // Get the sanitized error message
  const sanitized = sanitizeErrorMessage(error, '');

  // If we have a specific message, use it
  if (sanitized && sanitized !== fallback) {
    return sanitized;
  }

  // Otherwise, use status code message if available
  const statusCode = error?.response?.status;
  if (statusCode) {
    return getErrorMessageByStatus(statusCode, fallback);
  }

  return fallback;
}
